<template>
  <section class="assets-panel">
    <article class="mobile-asset-summary">
      <span>{{ $t('总资产 (USDT)') }}</span>
      <strong>{{ money(balances.total) }}</strong>
      <small>≈ ¥ {{ convertedTotal }}</small>
      <i class="mobile-asset-summary__bar">
        <b
          v-for="item in allocation"
          :key="`mobile-allocation-${item.key}`"
          :style="{ width: `${item.percent}%`, background: item.color }"
        />
      </i>
      <div class="mobile-asset-summary__legend">
        <span v-for="item in allocation" :key="`mobile-legend-${item.key}`">
          <i :style="{ background: item.color }" />{{ item.name }} {{ item.percent }}%
        </span>
      </div>
    </article>

    <div class="section-heading">
      <div>
        <h1>{{ $t('资产') }}</h1>
        <p>{{ $t(hideFuelAccount ? '资金与点卡账户集中管理' : '资金、燃料与点卡账户集中管理') }}</p>
      </div>
      <div class="section-heading__actions">
        <button class="up-ghost-button" type="button" :disabled="refreshing" @click="$emit('refresh')">
          {{ $t('刷新资产') }}
        </button>
        <button v-if="!hideFuelAccount" class="up-ghost-button" type="button" @click="$emit('action', 'transfer')">
          {{ $t('账户划转') }}
        </button>
        <button class="up-primary-button" type="button" @click="$emit('action', 'deposit')">
          {{ $t('充值') }}
        </button>
      </div>
    </div>

    <div class="account-grid">
      <article class="up-card asset-summary">
        <span class="asset-summary__label">{{ $t('总资产 (USDT)') }}</span>
        <strong class="asset-summary__total">{{ money(balances.total) }}</strong>
        <small>≈ ¥ {{ convertedTotal }}</small>
        <EChartDonut
          v-if="allocation.length"
          class="asset-summary__donut"
          :items="allocation"
          :total="roundedTotal"
          :unit="$t('USDT 总资产')"
          :aria-label="$t(hideFuelAccount ? '资金账户和点卡账户资产分布' : '燃料账户、资金账户和点卡账户资产分布')"
        />
        <div v-else class="asset-summary__empty">{{ $t('暂无资产分布数据') }}</div>
        <div class="asset-summary__legend">
          <div v-for="item in allocation" :key="item.key">
            <span><i :style="{ background: item.color }" />{{ item.name }}</span>
            <strong>{{ money(item.amount) }} <small>· {{ item.percent }}%</small></strong>
          </div>
        </div>
        <div class="asset-summary__sync"><i />{{ syncText }}</div>
      </article>

      <article v-for="account in visibleAccounts" :key="account.key" class="up-card account-card">
        <div class="account-card__heading">
          <span class="account-card__icon" :class="account.tone">
            <img :src="account.icon" alt="" aria-hidden="true">
          </span>
          <h2>{{ $t(account.name) }}</h2>
        </div>
        <div class="account-card__amount">
          <strong>{{ money(balances[account.key]) }}</strong>
        </div>
        <small>
          ≈ ¥ {{ converted(balances[account.key]) }}
        </small>
        <p>{{ $t(account.caption) }}</p>
        <div class="account-card__actions">
          <button
            v-for="action in account.actions"
            :key="action.key"
            :class="{ primary: action.primary }"
            type="button"
            @click="handleAccountAction(action, account)"
          >
            {{ $t(action.label) }}
          </button>
        </div>
      </article>
    </div>

    <div class="orders-heading">
      <i />
      <h2>{{ $t('充值提现订单') }}</h2>
      <span>{{ $t('资金流水') }}</span>
    </div>

    <article class="up-card orders-card">
      <div class="orders-toolbar">
        <div class="segmented">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            :class="{ active: activeTab === tab.key }"
            type="button"
            @click="changeOrderTab(tab.key)"
          >
            {{ $t(tab.label) }}
          </button>
        </div>
        <div class="orders-toolbar__actions">
          <select v-model="statusFilter" :aria-label="$t('订单状态')">
            <option v-for="option in statusOptions" :key="option.value" :value="option.value">
              {{ $t(option.label) }}
            </option>
          </select>
          <button type="button" :disabled="loading" @click="refreshCurrentOrders">
            {{ $t('刷新') }}
          </button>
        </div>
      </div>

      <div class="order-table__head">
        <span>{{ $t('时间') }}</span>
        <span>{{ $t('账户') }}</span>
        <span>{{ $t('币种') }}</span>
        <span>{{ $t('数量') }}</span>
        <span>{{ $t('渠道 · 地址') }}</span>
        <span>{{ $t('状态') }}</span>
      </div>
      <div class="order-table__body">
        <div v-for="row in visibleRecords" :key="row.id" class="order-table__row">
          <span :data-label="$t('时间')">{{ orderTime(row.time) }}</span>
          <span :data-label="$t('账户')">{{ $t(row.account) }}</span>
          <strong :data-label="$t('币种')">{{ recordCoin(row) }}</strong>
          <strong :data-label="$t('数量')" :class="amountTone(row.amountValue)">{{ amountText(row.amountValue) }}</strong>
          <span :data-label="$t('渠道 · 地址')" class="order-table__channel" :title="recordChannel(row, false)">
            {{ recordChannel(row) }}
          </span>
          <span :data-label="$t('状态')"><i class="order-status" :class="statusClass(row.status)">{{ $t(row.status) }}</i></span>
        </div>
        <div v-for="row in visibleRecords" :key="`mobile-order-${row.id}`" class="mobile-order-row">
          <div class="mobile-order-row__main">
            <strong>{{ $t(row.category === 'withdraw' ? '提现' : '充值') }}</strong>
            <small>{{ mobileOrderTime(row.time) }} · {{ recordChannel(row) }}</small>
          </div>
          <div class="mobile-order-row__result">
            <strong :class="amountTone(row.amountValue)">{{ amountText(row.amountValue) }}</strong>
            <i class="order-status" :class="statusClass(row.status)">{{ $t(row.status) }}</i>
          </div>
        </div>
        <div v-if="!visibleRecords.length" class="record-card__empty">
          <i :class="loading ? 'el-icon-loading' : 'el-icon-document'" />
          <span>{{ loading ? $t('正在同步订单…') : $t('暂无订单数据') }}</span>
        </div>
      </div>
      <div v-if="currentOrderTotal > 0" class="orders-pagination-bar">
        <span>{{ $t(`共 ${currentOrderTotal} 条，每页 ${currentOrderPageSize} 条`) }}</span>
        <el-pagination
          class="orders-pagination"
          background
          layout="prev, pager, next"
          :current-page="currentOrderPage"
          :page-size="currentOrderPageSize"
          :total="currentOrderTotal"
          :disabled="loading"
          @current-change="handleOrderPageChange"
        />
      </div>
      <footer>{{ $t('充值与提现订单与账单页资金流水保持一致，可交叉核对。') }}</footer>
    </article>
  </section>
</template>

<script>
import EChartDonut from '@/components/rebuild/EChartDonut.vue'
import fundAccountIcon from '@/assets/rebuild/asset-account-fund.svg'
import fuelAccountIcon from '@/assets/rebuild/asset-account-fuel.svg'
import cardAccountIcon from '@/assets/rebuild/asset-account-card.svg'
import {
  finiteNumber,
  firstText,
  formatNumber,
  formatSigned
} from '@/utils/portal-data'

export default {
  name: 'AssetsPanel',
  components: { EChartDonut },
  props: {
    balances: { type: Object, required: true },
    records: { type: Array, default: () => [] },
    userInfo: { type: Object, default: () => ({}) },
    earnings: { type: Object, required: true },
    rate: { type: Number, default: null },
    loading: { type: Boolean, default: false },
    refreshing: { type: Boolean, default: false },
    lastSync: { type: Date, default: null },
    hideFuelAccount: { type: Boolean, default: false },
    orderPagination: {
      type: Object,
      default: () => ({
        deposit: { page: 1, pageSize: 10, total: 0 },
        withdraw: { page: 1, pageSize: 10, total: 0 }
      })
    }
  },
  data() {
    return {
      activeTab: 'deposit',
      statusFilter: 'all',
      tabs: [
        { key: 'deposit', label: '充值订单' },
        { key: 'withdraw', label: '提现订单' }
      ],
      statusOptions: [
        { value: 'all', label: '全部状态' },
        { value: 'completed', label: '已完成' },
        { value: 'processing', label: '处理中' },
        { value: 'review', label: '审核中' }
      ],
      accounts: [
        {
          key: 'fund',
          name: '资金账户',
          caption: '用于充值、提现与内部划转。',
          icon: fundAccountIcon,
          tone: 'cyan',
          actions: [
            { key: 'deposit', label: '充值', primary: true },
            { key: 'withdraw', label: '提现' },
            { key: 'transfer', label: '划转' }
          ]
        },
        {
          key: 'fuel',
          name: '燃料账户',
          caption: '用于机器人运行与平台服务结算。',
          icon: fuelAccountIcon,
          tone: 'blue',
          actions: [
            { key: 'transfer', label: '划转', primary: true },
            { key: 'bills', label: '查看账单', navigate: 'bills', query: { category: 'profit' } }
          ]
        },
        {
          key: 'card',
          name: '点卡账户',
          caption: '用于抵扣平台服务费，消费时优先扣除。',
          icon: cardAccountIcon,
          tone: 'green',
          actions: [
            { key: 'buy-card', label: '购买点卡', primary: true },
            { key: 'fee-records', label: '消费记录', navigate: 'bills', query: { category: 'fee' } }
          ]
        }
      ]
    }
  },
  computed: {
    allocation() {
      const definitions = [
        { key: 'fuel', name: this.$t('燃料账户'), color: '#1F6BFF' },
        { key: 'fund', name: this.$t('资金账户'), color: '#0EA5E9' },
        { key: 'card', name: this.$t('点卡账户 (折算)'), color: '#0E9F5C' }
      ].filter(item => !this.hideFuelAccount || item.key !== 'fuel')
      const rows = definitions
        .map(item => ({ ...item, amount: finiteNumber(this.balances[item.key]) }))
        .filter(item => item.amount !== null)
      const total = rows.reduce((sum, item) => sum + item.amount, 0)
      return rows.map(item => ({
        ...item,
        percent: total ? formatNumber(item.amount / total * 100, 1) : '0.0'
      }))
    },
    visibleAccounts() {
      const accounts = this.hideFuelAccount
        ? this.accounts.filter(item => item.key !== 'fuel')
        : this.accounts
      if (!this.hideFuelAccount) return accounts
      return accounts.map(account => ({
        ...account,
        actions: account.actions.filter(action => action.key !== 'transfer')
      }))
    },
    visibleRecords() {
      return this.records.filter(row => {
        if (row.category !== this.activeTab) return false
        return this.statusFilter === 'all' || this.statusValue(row.status) === this.statusFilter
      }).slice(0, this.currentOrderPageSize)
    },
    currentOrderPagination() {
      return this.orderPagination[this.activeTab] || { page: 1, pageSize: 10, total: 0 }
    },
    currentOrderPage() {
      return Number(this.currentOrderPagination.page) || 1
    },
    currentOrderPageSize() {
      return Number(this.currentOrderPagination.pageSize) || 10
    },
    currentOrderTotal() {
      return Number(this.currentOrderPagination.total) || 0
    },
    convertedTotal() {
      const total = finiteNumber(this.balances.total)
      if (total === null || this.rate === null) return '--'
      return formatNumber(total * this.rate)
    },
    roundedTotal() {
      return formatNumber(this.balances.total, 0)
    },
    syncText() {
      if (finiteNumber(this.balances.total) === null || !this.lastSync) {
        return this.refreshing ? this.$t('资产数据同步中') : this.$t('资产数据尚未同步')
      }
      const now = new Date()
      const sameDay = now.toDateString() === this.lastSync.toDateString()
      const time = `${String(this.lastSync.getHours()).padStart(2, '0')}:${String(this.lastSync.getMinutes()).padStart(2, '0')}`
      const date = sameDay
        ? this.$t('今天')
        : `${String(this.lastSync.getMonth() + 1).padStart(2, '0')}-${String(this.lastSync.getDate()).padStart(2, '0')}`
      return `${this.$t('链上数据已同步')} · ${date} ${time}`
    }
  },
  methods: {
    money(value) {
      return formatNumber(value)
    },
    converted(value) {
      const amount = finiteNumber(value)
      return amount === null || this.rate === null ? '--' : formatNumber(amount * this.rate)
    },
    handleAccountAction(action, account) {
      if (action.navigate) {
        this.$emit('navigate', action.navigate, action.query || {})
        return
      }
      this.$emit('action', {
        type: action.key,
        account: account.key
      })
    },
    handleOrderPageChange(page) {
      this.$emit('order-page', {
        category: this.activeTab,
        page
      })
    },
    changeOrderTab(category) {
      this.activeTab = category
      this.statusFilter = 'all'
    },
    refreshCurrentOrders() {
      this.$emit('refresh-records', {
        category: this.activeTab,
        page: this.currentOrderPage
      })
    },
    orderTime(value) {
      return String(value || '--')
    },
    mobileOrderTime(value) {
      const text = String(value || '').trim()
      if (!text) return '--'
      const matched = text.match(/(?:\d{4}[-\/]?)?(\d{2})[-\/](\d{2})[ T](\d{2}):(\d{2})/)
      return matched ? `${matched[1]}-${matched[2]} ${matched[3]}:${matched[4]}` : text
    },
    recordCoin(row) {
      if (row.coin) return row.coin
      const coin = firstText(row.raw || {}, ['coin', 'currency', 'token', 'symbol', 'coin_name'])
      if (coin) return coin
      return '--'
    },
    recordChannel(row, masked = true) {
      const raw = row.raw || {}
      const network = firstText(raw, [
        'main_chain_detail',
        'main_chain',
        'transactionType',
        'chain_type',
        'chainType',
        'chain',
        'network',
        'protocol'
      ])
      const address = firstText(raw, [
        'chongzhi_url',
        'cash_address',
        'address',
        'to_address',
        'from_address',
        'wallet_address',
        'walletAddress'
      ])
      const networkText = String(network || '').replace(/\s+\(/g, '(')
      const addressText = masked ? this.maskAddress(address) : address
      const parts = [networkText, addressText].filter(Boolean)
      return parts.length ? parts.join(' · ') : '--'
    },
    maskAddress(value) {
      const text = String(value || '')
      if (!text || text.length <= 12) return text
      return `${text.slice(0, 6)}…${text.slice(-4)}`
    },
    amountText(value) {
      return formatSigned(value, 2)
    },
    amountTone(value) {
      const amount = finiteNumber(value)
      if (amount === null || amount === 0) return 'neutral'
      return amount > 0 ? 'positive' : 'negative'
    },
    statusValue(status) {
      const text = String(status || '')
      if (/待支付/.test(text)) return 'unpaid'
      if (/完成|成功|到账/.test(text)) return 'completed'
      if (/审核/.test(text)) return 'review'
      if (/处理中|待处理|进行中|提交/.test(text)) return 'processing'
      return 'other'
    },
    statusClass(status) {
      return `is-${this.statusValue(status)}`
    }
  }
}
</script>

<style scoped>
.assets-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: #171b25;
}

.section-heading {
  display: flex;
  min-height: 56px;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
}

.section-heading h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: .2px;
  line-height: 1.2;
}

.section-heading p {
  margin: 5px 0 0;
  color: #8a90a0;
  font-size: 13px;
}

.section-heading__actions {
  display: flex;
  gap: 10px;
}

.section-heading__actions button {
  height: 40px;
  min-width: 90px;
  padding: 0 16px;
  font-size: 12px;
}

.section-heading__actions button:disabled,
.orders-toolbar__actions button:disabled {
  cursor: not-allowed;
  opacity: .65;
}

.account-grid {
  display: grid;
  grid-template-columns: minmax(330px, 1.65fr) repeat(3, minmax(200px, 1fr));
  gap: 20px;
}

.asset-summary,
.account-card {
  height: 460px;
  min-width: 0;
  border-radius: 14px;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, .04),
    0 6px 20px rgba(16, 24, 40, .04);
}

.asset-summary {
  display: flex;
  padding: 22px;
  flex-direction: column;
}

.asset-summary__label,
.asset-summary > small,
.account-card > small {
  color: #8a90a0;
  font-size: 13px;
}

.asset-summary__label {
  font-size: 13.5px;
  font-weight: 500;
}

.asset-summary__total {
  margin-top: 7px;
  color: #171b25;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -.3px;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}

.asset-summary > small {
  margin-top: 5px;
}

.asset-summary__donut {
  width: 170px;
  height: 170px;
  min-height: 170px;
  margin: 18px auto 10px;
}

.asset-summary__empty {
  display: grid;
  height: 216px;
  place-items: center;
  color: #a0a8b8;
  font-size: 12px;
}

.asset-summary__legend {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.asset-summary__legend > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  color: #3a3f4e;
  font-size: 13.5px;
}

.asset-summary__legend span {
  display: inline-flex;
  min-width: 0;
  align-items: center;
}

.asset-summary__legend span i {
  width: 9px;
  height: 9px;
  margin-right: 9px;
  flex: 0 0 auto;
  border-radius: 3px;
}

.asset-summary__legend strong {
  font-size: 13.5px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.asset-summary__legend strong small {
  color: #a2a8b4;
  font-size: 13.5px;
  font-weight: 500;
}

.asset-summary__sync {
  display: flex;
  margin-top: 16px;
  padding-top: 12px;
  align-items: center;
  color: #0b7a47;
  border-top: 1px solid #f3f4f8;
  font-size: 12.5px;
}

.asset-summary__sync i {
  width: 7px;
  height: 7px;
  margin-right: 8px;
  border-radius: 50%;
  background: #12b76a;
}

.account-card {
  display: flex;
  padding: 22px;
  flex-direction: column;
}

.account-card__heading {
  display: flex;
  align-items: center;
  gap: 10px;
}

.account-card__heading h2 {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
}

.account-card__icon {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border-radius: 10px;
}

.account-card__icon.cyan {
  background: #e0f4fd;
}

.account-card__icon.blue {
  background: #ebf1fe;
}

.account-card__icon.green {
  background: #e8f6ee;
}

.account-card__icon img {
  display: block;
  width: 18px;
  height: 18px;
}

.account-card__amount {
  display: flex;
  margin-top: 8px;
  align-items: baseline;
  gap: 5px;
}

.account-card__amount strong {
  color: #171b25;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: normal;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
}

.account-card > small {
  margin-top: 4px;
  font-size: 12.5px;
}

.account-card p {
  margin: 4px 0 0;
  color: #79808f;
  font-size: 12.5px;
  line-height: 21.25px;
}

.account-card__actions {
  display: flex;
  margin-top: auto;
  gap: 8px;
}

.account-card__actions button {
  height: 36px;
  min-width: 0;
  padding: 0 15px;
  flex: 1;
  color: #303746;
  border: 1px solid #dce1e9;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  transition: border-color .16s ease, color .16s ease, background .16s ease;
}

.account-card__actions button:hover {
  color: #1f6bff;
  border-color: #9fbdff;
}

.account-card__actions button.primary {
  color: #fff;
  border-color: #1f6bff;
  background: #1f6bff;
}

.account-card__actions button.primary:hover {
  border-color: #175bdc;
  background: #175bdc;
}

.orders-heading {
  display: flex;
  height: 34px;
  align-items: center;
  gap: 10px;
}

.orders-heading > i {
  width: 4px;
  height: 22px;
  border-radius: 2px;
  background: #1f6bff;
}

.orders-heading h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: .2px;
}

.orders-heading span {
  padding: 3px 11px;
  color: #1f6bff;
  border-radius: 999px;
  background: #edf3ff;
  font-size: 11.5px;
  font-weight: 800;
}

.orders-card {
  overflow: hidden;
  border-radius: 14px;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, .04),
    0 6px 20px rgba(16, 24, 40, .04);
}

.mobile-order-row {
  display: none;
}

.orders-toolbar {
  display: flex;
  height: 70px;
  padding: 18px 24px 14px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.segmented {
  display: flex;
  gap: 4px;
  padding: 3px;
  border-radius: 9px;
  background: #f5f6f9;
}

.segmented button {
  min-width: 84px;
  height: 32px;
  padding: 7px 16px;
  color: #6f7787;
  border: 0;
  border-radius: 7px;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
}

.segmented button.active {
  color: #1f6bff;
  background: #fff;
  box-shadow: 0 1px 4px rgba(16, 24, 40, .08);
}

.orders-toolbar__actions {
  display: flex;
  gap: 10px;
}

.orders-toolbar__actions select,
.orders-toolbar__actions button {
  height: 36px;
  color: #343b49;
  border: 1px solid #dce1e9;
  border-radius: 8px;
  outline: none;
  background-color: #fff;
  font-size: 13px;
  font-weight: 600;
}

.orders-toolbar__actions select {
  min-width: 94px;
  height: 38px;
  padding: 8px 34px 8px 10px;
}

.orders-toolbar__actions button {
  min-width: 56px;
  padding: 8px 14px;
}

.orders-toolbar__actions button:hover,
.orders-toolbar__actions select:focus {
  color: #1f6bff;
  border-color: #9fbdff;
}

.order-table__head,
.order-table__row {
  display: grid;
  grid-template-columns: 1fr 1fr .7fr 1fr 1.8fr .9fr;
  align-items: center;
  gap: 10px;
  padding-right: 24px;
  padding-left: 24px;
}

.order-table__head {
  height: 34px;
  padding-top: 8px;
  padding-bottom: 8px;
  color: #8a90a0;
  border-top: 1px solid #eef1f5;
  border-bottom: 1px solid #eef1f5;
  background: transparent;
  font-size: 12.5px;
  font-weight: 600;
}

.order-table__row {
  height: 48px;
  padding-top: 14px;
  padding-bottom: 14px;
  color: #5f6675;
  border-bottom: 1px solid #f0f2f6;
  font-size: 14px;
}

.order-table__row strong {
  color: #252b36;
  font-size: 14px;
  font-weight: 700;
}

.order-table__row .positive {
  color: #159765;
}

.order-table__row .negative {
  color: #e5484d;
}

.order-table__row .neutral {
  color: #667085;
}

.order-table__channel {
  overflow: hidden;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-status {
  display: inline-flex;
  min-width: 56px;
  height: 25px;
  padding: 0 10px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 10.5px;
  font-style: normal;
  font-weight: 700;
}

.order-status.is-completed {
  color: #0b7a47;
  background: #e8f6ee;
}

.order-status.is-processing,
.order-status.is-review {
  color: #b26b00;
  background: #fff4df;
}

.order-status.is-unpaid {
  color: #e5484d;
  background: #fdecec;
}

.order-status.is-other {
  color: #667085;
  background: #f1f3f6;
}

.record-card__empty {
  display: grid;
  min-height: 160px;
  place-content: center;
  color: #98a0af;
  text-align: center;
  font-size: 12px;
}

.record-card__empty i {
  margin-bottom: 8px;
  font-size: 24px;
}

.orders-pagination-bar {
  display: flex;
  min-height: 58px;
  padding: 14px 24px 4px;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f0f2f6;
}

.orders-pagination-bar > span {
  color: #79808f;
  font-size: 12px;
}

.orders-pagination {
  display: flex;
}

::v-deep .orders-pagination.is-background .btn-prev,
::v-deep .orders-pagination.is-background .btn-next,
::v-deep .orders-pagination.is-background .el-pager li {
  min-width: 30px;
  height: 30px;
  border-radius: 7px;
  background: #f5f7fa;
  color: #5f6675;
  line-height: 30px;
}

::v-deep .orders-pagination.is-background .el-pager li:not(.disabled).active {
  background: #1f6bff;
  color: #fff;
}

.orders-card footer {
  min-height: 45px;
  padding: 14px 24px;
  color: #98a0af;
  font-size: 12.5px;
}

@media (max-width: 1200px) {
  .account-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .asset-summary {
    height: auto;
    min-height: 430px;
    grid-column: 1 / 4;
  }

  .account-card {
    height: 360px;
  }

  .order-table__head,
  .order-table__row {
    grid-template-columns: 125px 110px 80px 115px minmax(170px, 1fr) 90px;
  }
}

@media (max-width: 840px) {
  .account-grid {
    grid-template-columns: 1fr;
  }

  .asset-summary {
    grid-column: auto;
  }

  .account-card {
    height: 300px;
  }

  .order-table__head {
    display: none;
  }

  .order-table__row {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    padding-top: 12px;
    padding-bottom: 12px;
  }
}

@media (max-width: 640px) {
  .section-heading,
  .orders-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .section-heading__actions,
  .orders-toolbar__actions {
    width: 100%;
  }

  .section-heading__actions button,
  .orders-toolbar__actions > * {
    flex: 1;
  }

  .asset-summary,
  .account-card {
    height: auto;
    min-height: 390px;
  }

  .order-table__row {
    grid-template-columns: 1fr 1fr;
  }

  .order-table__row > :nth-child(5) {
    grid-column: 1 / 3;
  }
}
</style>
